/**
 * VideoPlayer — strict 3-tier fallback with adaptive quality + 10s preload.
 *   Tier 1: native <video> from S3 (mobile-optimized URL on slow networks)
 *   Tier 2: YouTube embed by `ytId` (with IFrame API error detection)
 *   Tier 3: "Video coming soon" placeholder
 *
 * Transitions are silent — no spinners, no error toasts.
 *
 * Props:
 *   s3Url        — full quality MP4 URL (e.g. .../back_001.mp4)
 *   s3MobileUrl  — mobile-optimized 720p MP4 URL (e.g. .../back_001_mobile.mp4)
 *   ytId         — YouTube fallback ID
 *   thumbnail    — poster image
 *   exerciseName — for placeholder copy
 *   nextS3Url    — preload first 10s of next exercise's video (mobile-data) or full (wifi)
 */
import { useEffect, useId, useRef, useState } from "react";

function pickConnection() {
  // Returns {effective, downlink, fast, mobile}
  if (typeof navigator === "undefined") return { fast: true, mobile: false };
  const c =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;
  if (!c) return { fast: true, mobile: false };
  const eff = c.effectiveType || "4g";
  const fast = eff === "4g" || eff === "5g";
  const mobile = eff === "2g" || eff === "3g" || eff === "slow-2g" || c.saveData;
  return { fast, mobile, effective: eff, downlink: c.downlink };
}

export default function VideoPlayer({ s3Url, s3MobileUrl, ytId, thumbnail, exerciseName, nextS3Url, nextS3MobileUrl }) {
  // initial tier: prefer s3 if provided, else yt, else placeholder
  const initial = s3Url || s3MobileUrl ? "s3" : ytId ? "yt" : "placeholder";
  const [tier, setTier] = useState(initial);
  const [lowQuality, setLowQuality] = useState(false);
  const playerHostId = useId().replace(/[^a-zA-Z0-9]/g, "");
  const ytPlayerRef = useRef(null);
  const preloadRef = useRef(null);

  // Determine which S3 URL to play based on connection
  const conn = pickConnection();
  const playUrl = (() => {
    if (!s3Url && !s3MobileUrl) return null;
    if (conn.mobile && s3MobileUrl) return s3MobileUrl;
    if (s3Url) return s3Url;
    return s3MobileUrl;
  })();

  // Reset when source changes
  useEffect(() => {
    setTier(s3Url || s3MobileUrl ? "s3" : ytId ? "yt" : "placeholder");
    setLowQuality(conn.mobile && !!s3MobileUrl);
  }, [s3Url, s3MobileUrl, ytId, conn.mobile]);

  // Preload next exercise's video (10s on mobile, full on wifi)
  useEffect(() => {
    if (!nextS3Url && !nextS3MobileUrl) return;
    const url = conn.mobile ? nextS3MobileUrl || nextS3Url : nextS3Url || nextS3MobileUrl;
    if (!url) return;
    if (preloadRef.current) {
      try { preloadRef.current.src = ""; } catch {}
    }
    const v = document.createElement("video");
    v.preload = conn.mobile ? "metadata" : "auto";
    v.muted = true;
    v.playsInline = true;
    v.src = url;
    if (conn.mobile) {
      // Limit preload to first ~10 seconds via load() then pause
      v.addEventListener("loadedmetadata", () => {
        try {
          v.currentTime = 0;
          v.load();
        } catch {}
      });
    }
    preloadRef.current = v;
    return () => {
      try {
        if (preloadRef.current) {
          preloadRef.current.src = "";
          preloadRef.current.load();
          preloadRef.current = null;
        }
      } catch {}
    };
  }, [nextS3Url, nextS3MobileUrl, conn.mobile]);

  // ----- YouTube IFrame API tier -----
  useEffect(() => {
    if (tier !== "yt" || !ytId) return;

    let destroyed = false;

    const ensureApi = () => {
      return new Promise((resolve) => {
        if (window.YT && window.YT.Player) return resolve(window.YT);
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (typeof prev === "function") {
            try { prev(); } catch {}
          }
          resolve(window.YT);
        };
        if (!document.getElementById("yt-iframe-api")) {
          const tag = document.createElement("script");
          tag.id = "yt-iframe-api";
          tag.src = "https://www.youtube.com/iframe_api";
          document.body.appendChild(tag);
        }
      });
    };

    ensureApi().then((YT) => {
      if (destroyed || !YT) return;
      const host = document.getElementById(`yt-host-${playerHostId}`);
      if (!host) return;
      try {
        ytPlayerRef.current = new YT.Player(host, {
          videoId: ytId,
          playerVars: {
            autoplay: 1,
            modestbranding: 1,
            rel: 0,
            playsinline: 1,
            mute: 1,
            controls: 1,
          },
          events: {
            onError: () => setTier("placeholder"),
          },
        });
      } catch {
        setTier("placeholder");
      }
    });

    return () => {
      destroyed = true;
      try {
        ytPlayerRef.current?.destroy?.();
      } catch {}
      ytPlayerRef.current = null;
    };
  }, [tier, ytId, playerHostId]);

  return (
    <div data-testid="video-player-wrapper">
      <div
        className="w-full aspect-video rounded-[var(--radius-md)] overflow-hidden relative"
        data-testid="video-player"
        style={{ background: "var(--bg-inset)" }}
      >
        {tier === "s3" && playUrl && (
          <video
            key={playUrl}
            src={playUrl}
            poster={thumbnail}
            autoPlay
            loop
            muted
            playsInline
            controls
            preload="auto"
            onError={() => setTier(ytId ? "yt" : "placeholder")}
            className="absolute inset-0 w-full h-full object-cover bg-black"
          />
        )}

        {tier === "yt" && ytId && (
          <div
            id={`yt-host-${playerHostId}`}
            className="absolute inset-0 w-full h-full"
            aria-label={exerciseName ? `${exerciseName} demonstration` : "Exercise video"}
          />
        )}

        {tier === "placeholder" && (
          <div
            className="absolute inset-0 grid place-items-center"
            style={{ background: "var(--bg-inset)", color: "var(--text-tertiary)" }}
          >
            <div className="text-center px-6">
              <div
                className="font-display text-[18px] sm:text-[20px]"
                style={{ color: "var(--text-tertiary)", letterSpacing: "-0.01em" }}
              >
                Video coming soon
              </div>
              {exerciseName && (
                <div
                  className="text-[12.5px] mt-1"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  {exerciseName}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {tier === "s3" && lowQuality && (
        <div
          className="mt-1.5 text-center"
          data-testid="low-quality-notice"
          style={{
            fontFamily: "Manrope",
            fontSize: 11,
            fontWeight: 400,
            color: "var(--text-tertiary)",
          }}
        >
          Low quality video due to connection
        </div>
      )}
    </div>
  );
}
